/**
 * useRecentSearches.ts — localStorage-backed recent address searches (T-012/W1 Task 5).
 *
 * Stores the last MAX successful search queries (most-recent-first, deduped) under
 * a single localStorage key. Mirrors the prototype's recent-searches dropdown.
 *
 *   add(q)  — prepend q, drop any earlier duplicate, cap at MAX, persist.
 *   clear() — wipe the list + the localStorage key.
 */
import { useState } from 'react'

const KEY = 'optiyield_recent_searches'
const MAX = 5

function read(): string[] {
  try {
    const raw = localStorage.getItem(KEY)
    const parsed = raw ? (JSON.parse(raw) as unknown) : []
    return Array.isArray(parsed) ? (parsed as string[]) : []
  } catch {
    return []
  }
}

export function useRecentSearches() {
  const [items, setItems] = useState<string[]>(() => read())

  const add = (q: string) => {
    const query = q.trim()
    if (!query) return
    setItems((prev) => {
      const next = [query, ...prev.filter((x) => x !== query)].slice(0, MAX)
      try {
        localStorage.setItem(KEY, JSON.stringify(next))
      } catch {
        // localStorage unavailable (private mode / quota) — keep in-memory only
      }
      return next
    })
  }

  const clear = () => {
    try {
      localStorage.removeItem(KEY)
    } catch {
      // ignore
    }
    setItems([])
  }

  return { items, add, clear }
}
